import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Space, Table, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { FaqItemAdmin, CreateFaqItemRequest } from '../../types/faq.types';
import { faqService } from '../../services/faq.service';
import { FaqFormModal } from '../../components/admin/faq/FaqFormModal';

const { Paragraph } = Typography;

export const AdminFaqPage: React.FC = () => {
  const [items, setItems] = useState<FaqItemAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FaqItemAdmin | null>(null);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await faqService.listAdmin();
      setItems(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao carregar perguntas frequentes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const openCreate = () => {
    setEditingItem(null);
    setFormOpen(true);
  };

  const openEdit = (item: FaqItemAdmin) => {
    setEditingItem(item);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingItem(null);
  };

  const handleSave = async (values: CreateFaqItemRequest) => {
    try {
      setSaving(true);
      if (editingItem) {
        await faqService.update(editingItem.id, values);
        message.success('Pergunta atualizada com sucesso');
      } else {
        await faqService.create(values);
        message.success('Pergunta criada com sucesso');
      }
      closeForm();
      await loadItems();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Não foi possível salvar a pergunta');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (item: FaqItemAdmin) => {
    try {
      await faqService.update(item.id, { active: !item.active });
      message.success(item.active ? 'Pergunta inativada' : 'Pergunta reativada');
      await loadItems();
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Não foi possível alterar o status');
    }
  };

  const columns = [
    {
      title: 'Ordem',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 80,
    },
    {
      title: 'Pergunta',
      dataIndex: 'question',
      key: 'question',
      render: (text: string) => (
        <span title={text}>{text.length > 80 ? `${text.substring(0, 80)}...` : text}</span>
      ),
    },
    {
      title: 'Resposta',
      dataIndex: 'answer',
      key: 'answer',
      render: (text: string) => (
        <span title={text} style={{ color: 'rgba(0,0,0,0.45)' }}>
          {text.length > 60 ? `${text.substring(0, 60)}...` : text}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'active',
      width: 100,
      render: (_: unknown, record: FaqItemAdmin) => (
        <Badge
          status={record.active ? 'success' : 'default'}
          text={record.active ? 'Ativo' : 'Inativo'}
        />
      ),
    },
    {
      title: 'Atualização',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (value: string) => new Date(value).toLocaleString('pt-BR'),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 180,
      render: (_: unknown, record: FaqItemAdmin) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            Editar
          </Button>
          <Button
            size="small"
            danger={record.active}
            onClick={() => handleToggleStatus(record)}
          >
            {record.active ? 'Inativar' : 'Reativar'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          Perguntas Frequentes
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nova Pergunta
        </Button>
      </Space>

      <Paragraph type="secondary">
        Gerencie as perguntas e respostas exibidas na página pública de FAQ.
      </Paragraph>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={false}
      />

      <FaqFormModal
        open={formOpen}
        loading={saving}
        initialItem={editingItem}
        onCancel={closeForm}
        onSubmit={handleSave}
      />
    </div>
  );
};

export default AdminFaqPage;
